import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Paper,
  useMediaQuery,
  Theme,
  Button
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchServices } from 'slices/NdisSlices/platformAdmin/ManageServicesSlice';
import { Service, Role } from 'types/ndisSection/manageServicesTypes';
import { RootState } from 'app/store';
import Toast from 'commonComponents/Toast';
import { showToast } from 'store/slices/toastSlice';
import { fetchRoles } from 'slices/NdisSlices/platformAdmin/ManageRolesSlice';

const ServicesContent: React.FC = () => {
  const dispatch = useDispatch();
  const { services, loading, error } = useSelector((state: RootState) => state.manageServices);
  const { roles } = useSelector((state: RootState) => state.manageRoles);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  useEffect(() => {
    dispatch(fetchServices());
    dispatch(fetchRoles());
  }, [dispatch]);

  const showNotification = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    dispatch(showToast({ message, severity }));
  };

  const handleBookmark = (service: Service) => {
    showNotification('Service bookmarked!', 'success');
  };

  const handleRequestAccess = (service: Service) => {
    showNotification('Access requested!', 'success');
  };

  const getRoleLabel = (role_id: string) => {
    const role = roles.find((r) => r.role_id === role_id);
    return role ? role.label : role_id;
  };

  return (
    <div>
      <Box sx={{ p: isMobile ? 0 : 0, bgcolor: 'background.paper' }}>
        <Paper
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)'
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection={isMobile ? 'column' : 'row'}
            gap={2}
          >
            <Typography variant="h4" fontWeight="bold" color="primary">
              Services
            </Typography>
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : error ? (
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light' }}>
            <Typography color="error" variant="h6">
              {error}
            </Typography>
          </Paper>
        ) : services.length > 0 ? (
          <Grid container spacing={isMobile ? 2 : 3}>
            {services.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service.service_id}>
                <motion.div>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      borderLeft: `4px solid ${service.background}`,
                      '&:hover': {
                        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar
                          sx={{
                            bgcolor: service.background,
                            mr: 2,
                            width: 48,
                            height: 48,
                            fontSize: '1.5rem'
                          }}
                          src={service.icon}
                        />
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {service.header}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {service.shortDescription}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                        {service.roles.map((role_id) => (
                          <Chip
                            key={role_id}
                            label={getRoleLabel(role_id)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ borderRadius: 4 }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Created by: {service.created_by}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created at: {new Date(service.created_at).toLocaleString()}
                      </Typography>
                    </CardContent>
                    <Divider />
                    <Box
                      p={2}
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                      bgcolor="action.hover"
                    >
                      <Box display="flex" gap={1}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<BookmarkIcon />}
                          onClick={() => handleBookmark(service)}
                          sx={{ borderRadius: 1 }}
                        >
                          Bookmark
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<LockOpenIcon />}
                          onClick={() => handleRequestAccess(service)}
                          sx={{ borderRadius: 1 }}
                        >
                          Request Access
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No services available.
            </Typography>
          </Paper>
        )}

        <Toast />
      </Box>
    </div>
  );
};

export default ServicesContent;